import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Product } from "../types";
import { useAuth } from "../context/AuthContext";

interface ProductCardProps {
    product: Product;
    showAdminActions?: boolean;
    compact?: boolean;
    categoryImage?: string;
    onEdit?: (product: Product) => void;
    onDelete?: (product: Product) => void;
    onAddToCart?: (product: Product) => void;
}

const API_URL = process.env.REACT_APP_API_URL || "";

const ProductCard: React.FC<ProductCardProps> = ({
    product,
    showAdminActions,
    compact,
    categoryImage,
    onEdit,
    onDelete,
    onAddToCart,
}) => {
    const { isAuthenticated } = useAuth();
    const [isHovered, setIsHovered] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    // Priorise image primary > toute image > vidéo primary > toute vidéo
    const primaryMedia =
        product.media?.find((m) => m.is_primary && m.media_type === "image") ??
        product.media?.find((m) => m.media_type === "image") ??
        product.media?.find((m) => m.is_primary) ??
        product.media?.[0];

    const productMediaUrl = primaryMedia
        ? `${API_URL}/${primaryMedia.media_path}`
        : "/images/placeholder.jpg";

    const isVideo = !categoryImage && primaryMedia?.media_type === "video";
    const imageUrl = categoryImage || productMediaUrl;

    const unitLabels: Record<string, string> = {
        gramme: "g",
        unite: "u",
        kg: "kg",
    };

    const formatPriceWithQuantity = (
        price: number,
        quantity: number,
        unit: string,
    ) => {
        const qty = quantity > 0 ? quantity : 1;
        return `${price.toFixed(2)} EUR / ${qty}${unitLabels[unit] || unit}`;
    };

    const getActivePrices = () => {
        return product.prices?.filter((p) => p.is_active) || [];
    };

    const lowestPrice = () => {
        const prices = getActivePrices();
        if (prices.length > 0) {
            return Math.min(...prices.map((p) => p.price));
        }
        return product.price;
    };

    return (
        <div
            className="group relative bg-primary-light/30 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10 hover:border-accent/50 transition-all duration-500 hover:shadow-2xl hover:shadow-accent/10 hover:-translate-y-2"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Glow effect on hover */}
            <div className="absolute inset-0 bg-gradient-to-t from-accent/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

            {/* Image/Video container */}
            <div
                className={`relative overflow-hidden ${compact ? "aspect-[4/3]" : "aspect-square"}`}
            >
                {/* Loading skeleton — uniquement pour les images */}
                {!isVideo && !imageLoaded && (
                    <div className="absolute inset-0 bg-primary-light animate-pulse">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-[shimmer_2s_infinite]" />
                    </div>
                )}

                {isVideo ? (
                    <video
                        src={imageUrl}
                        autoPlay
                        muted
                        loop
                        playsInline
                        className={`w-full h-full object-cover transition-all duration-700 ${
                            isHovered ? "scale-110 brightness-110" : "scale-100"
                        }`}
                    />
                ) : (
                    <img
                        src={imageUrl}
                        alt={product.name}
                        className={`w-full h-full object-cover transition-all duration-700 ${
                            isHovered ? "scale-110 brightness-110" : "scale-100"
                        } ${imageLoaded ? "opacity-100" : "opacity-0"}`}
                        onLoad={() => setImageLoaded(true)}
                        onError={(e) => {
                            (e.target as HTMLImageElement).src =
                                "/images/placeholder.jpg";
                            setImageLoaded(true);
                        }}
                    />
                )}

                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {/* Unavailable overlay */}
                {!product.is_available && (
                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center backdrop-blur-sm">
                        <div className="text-center">
                            <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-red-500/20 flex items-center justify-center">
                                <svg
                                    className="w-6 h-6 text-red-400"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </div>
                            <span className="text-white font-semibold">
                                Indisponible
                            </span>
                        </div>
                    </div>
                )}

                {/* Top badges — cachés quand categoryImage est utilisé */}
                {!categoryImage && (
                    <div className="absolute top-2 left-2 right-2 z-10 flex items-center justify-between">
                        {product.is_available ? (
                            <span className="flex items-center space-x-0.5 bg-green-500/90 backdrop-blur-sm text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full">
                                <span className="w-1 h-1 bg-white rounded-full animate-pulse" />
                                <span>Stock</span>
                            </span>
                        ) : (
                            <span></span>
                        )}
                        <span className="bg-accent/90 backdrop-blur-sm text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-lg">
                            {product.category?.name}
                        </span>
                    </div>
                )}

                {/* Quick view button on hover */}
                {!showAdminActions && product.is_available && (
                    <div
                        className={`absolute inset-x-4 bottom-4 transition-all duration-500 ${isHovered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
                    >
                        <Link
                            to={`/products/${product.id}`}
                            className="flex items-center justify-center space-x-2 w-full bg-accent hover:bg-accent-light text-white font-semibold py-3 rounded-xl transition-all duration-300 shadow-lg hover:shadow-accent/50"
                        >
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                            </svg>
                            <span>Voir le produit</span>
                        </Link>
                    </div>
                )}
            </div>

            {/* Content */}
            <div className={compact ? "p-3" : "p-5"}>
                <h3
                    className={`text-white font-bold truncate group-hover:text-accent transition-colors duration-300 ${compact ? "text-base mb-1" : "text-lg mb-2"}`}
                >
                    {product.name}
                </h3>

                {product.description && !compact && (
                    <p className="text-gray-400 text-sm mb-4 line-clamp-2 leading-relaxed">
                        {product.description}
                    </p>
                )}

                <div className={compact ? "space-y-1" : "space-y-3"}>
                    <div className="flex items-baseline space-x-2">
                        <span
                            className={`font-bold text-accent ${compact ? "text-lg" : "text-2xl"}`}
                        >
                            {lowestPrice().toFixed(2)} EUR
                        </span>
                        {getActivePrices().length > 1 && (
                            <span className="text-gray-500 text-sm">et +</span>
                        )}
                    </div>

                    {getActivePrices().length > 0 && !compact && (
                        <div className="flex flex-wrap gap-2">
                            {getActivePrices()
                                .slice(0, 3)
                                .map((p, idx) => (
                                    <span
                                        key={idx}
                                        className="text-xs bg-accent/10 text-accent px-2 py-1 rounded-lg font-medium border border-accent/20"
                                    >
                                        {formatPriceWithQuantity(
                                            p.price,
                                            p.quantity,
                                            p.price_unit,
                                        )}
                                    </span>
                                ))}
                            {getActivePrices().length > 3 && (
                                <span className="text-xs text-gray-500 px-2 py-1">
                                    +{getActivePrices().length - 3} autres
                                </span>
                            )}
                        </div>
                    )}
                </div>

                <div
                    className={`border-t border-white/10 ${compact ? "mt-2 pt-2" : "mt-4 pt-4"}`}
                >
                    {!showAdminActions ? (
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Link
                                    to={`/products/${product.id}`}
                                    className="group/link flex items-center space-x-1 text-sm text-gray-400 hover:text-accent transition-colors"
                                >
                                    <span>Details</span>
                                    <svg
                                        className="w-4 h-4 transform group-hover/link:translate-x-1 transition-transform"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 5l7 7-7 7"
                                        />
                                    </svg>
                                </Link>
                                {product.is_available && (
                                    <div className="flex items-center space-x-0.5 text-green-400 text-[10px]">
                                        <svg
                                            className="w-2.5 h-2.5"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>
                                        <span>Dispo</span>
                                    </div>
                                )}
                            </div>
                            {onAddToCart && product.is_available && (
                                isAuthenticated ? (
                                    <button
                                        onClick={() => onAddToCart(product)}
                                        className="w-full flex items-center justify-center space-x-2 bg-accent hover:bg-accent-light text-white font-semibold text-sm py-2 rounded-xl transition-all duration-300"
                                    >
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 4v16m8-8H4"
                                            />
                                        </svg>
                                        <span>Ajouter au panier</span>
                                    </button>
                                ) : (
                                    <Link
                                        to="/login"
                                        className="w-full flex items-center justify-center space-x-2 bg-accent hover:bg-accent-light text-white font-semibold text-sm py-2 rounded-xl transition-all duration-300"
                                    >
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zM8 11V7a4 4 0 118 0v4"
                                            />
                                        </svg>
                                        <span>Connectez-vous</span>
                                    </Link>
                                )
                            )}
                        </div>
                    ) : (
                        <div className="flex space-x-3">
                            <button
                                onClick={() => onEdit?.(product)}
                                className="flex-1 flex items-center justify-center space-x-1 text-sm text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 py-2 rounded-lg transition-all"
                            >
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                    />
                                </svg>
                                <span>Modifier</span>
                            </button>
                            <button
                                onClick={() => onDelete?.(product)}
                                className="flex-1 flex items-center justify-center space-x-1 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 py-2 rounded-lg transition-all"
                            >
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                </svg>
                                <span>Supprimer</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes shimmer {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                }
            `}</style>
        </div>
    );
};

export default ProductCard;
